/**
 * @fileoverview Greeting messages utility for chat page
 * Provides random greeting messages with optional firstName interpolation
 */

export interface GreetingMessage {
  key: string;
  message: string;
  messageDE: string; // German translation
  messageIT: string; // Italian translation
  messageFR: string; // French translation
  useFirstName: boolean;
}

/**
 * Array of 10 different greeting messages
 * Some include firstName, others are general greetings
 */
export const GREETING_MESSAGES: GreetingMessage[] = [
  {
    key: 'GREETING_HELLO_FIRSTNAME',
    message: 'What can I do for you today, {firstName}?',
    messageDE: 'Was kann ich heute für dich tun, {firstName}?',
    messageIT: 'Come posso esserti utile oggi, {firstName}?',
    messageFR: "Que puis-je faire pour toi aujourd'hui, {firstName}?",
    useFirstName: true,
  },
  {
    key: 'GREETING_EXCITING_DAY',
    message: 'Hey there! What shall we explore today?',
    messageDE: 'Hey! Was erkunden wir heute?',
    messageIT: 'Ehi! Cosa esploriamo oggi?',
    messageFR: "Salut! Qu'allons-nous explorer aujourd'hui?",
    useFirstName: false,
  },
  {
    key: 'GREETING_WELCOME_FIRSTNAME',
    message: 'Good to have you back, {firstName}!',
    messageDE: 'Schön, dass du wieder da bist, {firstName}!',
    messageIT: 'Che bello riaverti qui, {firstName}!',
    messageFR: 'Ravi de te retrouver, {firstName}!',
    useFirstName: true,
  },
  {
    key: 'GREETING_READY_TO_HELP',
    message: 'Good to see you. What can I help you with?',
    messageDE: 'Schön, dich zu sehen. Womit kann ich dir helfen?',
    messageIT: 'Bello vederti. Come posso aiutarti?',
    messageFR: "Ravi de te voir. En quoi puis-je t'aider ?",
    useFirstName: false,
  },
  {
    key: 'GREETING_GREAT_DAY_FIRSTNAME',
    message: 'Hope your day is going great, {firstName}!',
    messageDE: 'Ich hoffe, dein Tag läuft gut, {firstName}!',
    messageIT: 'Spero che tu stia passando una bella giornata, {firstName}!',
    messageFR: "J'espère que ta journée se passe bien, {firstName}!",
    useFirstName: true,
  },
  {
    key: 'GREETING_LETS_CREATE',
    message: "Let's turn your ideas into reality.",
    messageDE: 'Lassen Sie uns Ihre Ideen in die Realität umsetzen.',
    messageIT: 'Trasformiamo le tue idee in realtà.',
    messageFR: 'Transformons vos idées en réalité.',
    useFirstName: false,
  },
  {
    key: 'GREETING_NICE_TO_SEE_FIRSTNAME',
    message: 'Always a pleasure, {firstName}!',
    messageDE: 'Immer eine Freude, {firstName}!',
    messageIT: 'È sempre un piacere, {firstName}!',
    messageFR: 'Toujours un plaisir, {firstName}!',
    useFirstName: true,
  },
  {
    key: 'GREETING_READY_FOR_ADVENTURE',
    message: 'Hi! Where should we dive in today?',
    messageDE: 'Hi! Wo tauchen wir heute ein?',
    messageIT: 'Ciao! Da dove partiamo oggi?',
    messageFR: "Salut! Par où commence-t-on aujourd'hui?",
    useFirstName: false,
  },
  {
    key: 'GREETING_WONDERFUL_DAY_FIRSTNAME',
    message: 'Good to see you, {firstName}. How can I assist you today?',
    messageDE: 'Schön, Sie zu sehen, {firstName}. Wie kann ich Ihnen heute helfen?',
    messageIT: 'Bello vederti, {firstName}. Come posso assisterti oggi?',
    messageFR: "Ravi de vous voir, {firstName}. Comment puis-je vous aider aujourd'hui ?",
    useFirstName: true,
  },
  {
    key: 'GREETING_LETS_GET_STARTED',
    message: "Ready to get started, {firstName}? Let's begin.",
    messageDE: 'Bereit loszulegen, {firstName}? Dann fangen wir an.',
    messageIT: 'Pronto per iniziare, {firstName}? Cominciamo.',
    messageFR: "Prêt à commencer, {firstName}? Allons-y.",
    useFirstName: true,
  },
];

/**
 * Gets a random greeting message
 * @param firstName - Optional first name to include in personalized greetings
 * @param language - Language code (e.g., 'en-US', 'de-DE', 'it-IT', 'fr-FR'). Defaults to 'en-US'
 * @returns A formatted greeting message
 */
export const getRandomGreeting = (firstName?: string, language = 'en-US'): string => {
  // Get a random message from the array
  const randomIndex = Math.floor(Math.random() * GREETING_MESSAGES.length);
  const selectedGreeting = GREETING_MESSAGES[randomIndex];
  
  // Determine which message to use based on language
  const isGerman = language.startsWith('de');
  const isItalian = language.startsWith('it');
  const isFrench = language.startsWith('fr');
  
  let messageText = selectedGreeting.message;
  if (isGerman) {
    messageText = selectedGreeting.messageDE;
  } else if (isItalian) {
    messageText = selectedGreeting.messageIT;
  } else if (isFrench) {
    messageText = selectedGreeting.messageFR;
  }
  
  // If the message uses firstName and we have a firstName, interpolate it
  if (selectedGreeting.useFirstName && firstName) {
    return messageText.replace('{firstName}', firstName);
  }
  
  // If the message uses firstName but we don't have one, fall back to a non-firstName message
  if (selectedGreeting.useFirstName && !firstName) {
    const nonFirstNameMessages = GREETING_MESSAGES.filter(msg => !msg.useFirstName);
    const fallbackIndex = Math.floor(Math.random() * nonFirstNameMessages.length);
    const fallbackGreeting = nonFirstNameMessages[fallbackIndex];
    
    let fallbackText = fallbackGreeting.message;
    if (isGerman) {
      fallbackText = fallbackGreeting.messageDE;
    } else if (isItalian) {
      fallbackText = fallbackGreeting.messageIT;
    } else if (isFrench) {
      fallbackText = fallbackGreeting.messageFR;
    }
    
    return fallbackText;
  }
  
  // Return the message as-is for non-firstName messages
  return messageText;
};

/**
 * Gets a specific greeting message by key
 * @param key - The greeting message key
 * @param firstName - Optional first name to include in personalized greetings
 * @param language - Language code (e.g., 'en-US', 'de-DE', 'it-IT', 'fr-FR'). Defaults to 'en-US'
 * @returns The formatted greeting message or null if key not found
 */
export const getGreetingByKey = (key: string, firstName?: string, language = 'en-US'): string | null => {
  const greeting = GREETING_MESSAGES.find(msg => msg.key === key);
  
  if (!greeting) {
    return null;
  }
  
  // Determine which message to use based on language
  const isGerman = language.startsWith('de');
  const isItalian = language.startsWith('it');
  const isFrench = language.startsWith('fr');
  
  let messageText = greeting.message;
  if (isGerman) {
    messageText = greeting.messageDE;
  } else if (isItalian) {
    messageText = greeting.messageIT;
  } else if (isFrench) {
    messageText = greeting.messageFR;
  }
  
  if (greeting.useFirstName && firstName) {
    return messageText.replace('{firstName}', firstName);
  }
  
  return messageText;
};
